import React, { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float } from '@react-three/drei';
import * as THREE from 'three';

const h = React.createElement;

// Cinematic narrow-lens framing — keeps the camera body prominent and reduces fish-eye distortion.
const CAMERA_FOV_DEGREES = 38;

/**
 * NOTE: Three.js primitives (mesh, group, cylinderGeometry...) are written via
 * React.createElement instead of JSX intrinsics. This avoids the visual-edits
 * babel plugin from injecting non-three.js metadata attributes onto R3F nodes.
 */
function AcousticCamera() {
  const group = useRef();
  const ring1 = useRef();
  const ring2 = useRef();

  useFrame((state, delta) => {
    if (group.current) group.current.rotation.y += delta * 0.25;
    if (ring1.current) ring1.current.rotation.z += delta * 0.6;
    if (ring2.current) ring2.current.rotation.z -= delta * 0.4;
  });

  const mics = [];
  for (let i = 0; i < 64; i++) {
    const angle = (i / 64) * Math.PI * 2;
    const r = 0.55 + (i % 3) * 0.18;
    mics.push(
      h('mesh', { key: i, position: [Math.cos(angle) * r, 0.275, Math.sin(angle) * r] },
        h('sphereGeometry', { args: [0.025, 8, 8] }),
        h('meshStandardMaterial', { color: '#222', metalness: 0.7, roughness: 0.3 })
      )
    );
  }

  return h('group', { ref: group },
    // Main body
    h('mesh', { castShadow: true, receiveShadow: true },
      h('cylinderGeometry', { args: [1.1, 1.1, 0.45, 64] }),
      h('meshPhysicalMaterial', { color: '#475569', metalness: 0.45, roughness: 0.45, clearcoat: 0.3 })
    ),
    // Sensor disc
    h('mesh', { position: [0, 0.24, 0] },
      h('cylinderGeometry', { args: [1.0, 1.0, 0.06, 64] }),
      h('meshPhysicalMaterial', { color: '#334155', metalness: 0.4, roughness: 0.6 })
    ),
    // Microphone array
    ...mics,
    // Center lens
    h('mesh', { position: [0, 0.29, 0] },
      h('cylinderGeometry', { args: [0.18, 0.18, 0.04, 32] }),
      h('meshPhysicalMaterial', { color: '#0891b2', emissive: '#0891b2', emissiveIntensity: 1.2, metalness: 0.9, roughness: 0.1 })
    ),
    // Glowing rings
    h('mesh', { ref: ring1, position: [0, 0.27, 0], rotation: [Math.PI / 2, 0, 0] },
      h('torusGeometry', { args: [0.95, 0.012, 16, 100] }),
      h('meshBasicMaterial', { color: '#0891b2', toneMapped: false })
    ),
    h('mesh', { ref: ring2, position: [0, 0.27, 0], rotation: [Math.PI / 2, 0, 0] },
      h('torusGeometry', { args: [0.78, 0.008, 16, 100] }),
      h('meshBasicMaterial', { color: '#0e7490', toneMapped: false })
    ),
    // Handle / grip
    h('mesh', { position: [0, -0.35, 0] },
      h('boxGeometry', { args: [0.6, 0.35, 0.6] }),
      h('meshPhysicalMaterial', { color: '#334155', metalness: 0.4, roughness: 0.65 })
    ),
    // Display panel
    h('mesh', { position: [0, -0.35, 0.305] },
      h('planeGeometry', { args: [0.45, 0.22] }),
      h('meshBasicMaterial', { color: '#0891b2', toneMapped: false, opacity: 0.7, transparent: true })
    )
  );
}

/**
 * Leak source — small pulsing red dot emitting concentric sound waves.
 * Represents the unsichtbare Leck the camera is detecting from a distance.
 */
function LeakSource({ position = [1.9, 1.5, 0.7] }) {
  const core = useRef();
  const halo = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (core.current && core.current.material) {
      core.current.material.emissiveIntensity = 2.2 + Math.sin(t * 6) * 0.9;
    }
    if (halo.current) {
      const s = 1 + Math.sin(t * 4) * 0.25;
      halo.current.scale.set(s, s, s);
    }
  });
  return h('group', { position },
    h('mesh', { ref: halo },
      h('sphereGeometry', { args: [0.09, 16, 16] }),
      h('meshBasicMaterial', { color: '#ff2a55', transparent: true, opacity: 0.18, toneMapped: false })
    ),
    h('mesh', { ref: core },
      h('sphereGeometry', { args: [0.05, 16, 16] }),
      h('meshStandardMaterial', {
        color: '#ff2a55',
        emissive: '#ff2a55',
        emissiveIntensity: 2.5,
        toneMapped: false,
      })
    )
  );
}

/**
 * Sound waves emanating from the leak as concentric horizontal rings
 * (sonar-style ripples) that the acoustic camera "picks up". Multiple rings
 * are phase-offset so the radiation looks continuous.
 */
function SoundWaves({ position = [1.9, 1.5, 0.7], count = 6, color = '#0891b2' }) {
  const refs = useRef([]);
  refs.current = [];

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const period = 2.6;
    refs.current.forEach((ring, i) => {
      if (!ring) return;
      const phase = ((t + (i * period) / count) % period) / period; // 0..1
      const scale = 0.35 + phase * 2.6;
      ring.scale.set(scale, scale, scale);
      if (ring.material) {
        ring.material.opacity = 0.7 * (1 - phase);
      }
    });
  });

  const rings = [];
  for (let i = 0; i < count; i++) {
    rings.push(
      h('mesh', {
        key: `r${i}`,
        ref: (el) => { if (el) refs.current[i] = el; },
        rotation: [Math.PI / 2, 0, 0], // horizontal ring (XZ plane)
      },
        h('ringGeometry', { args: [0.28, 0.34, 80] }),
        h('meshBasicMaterial', {
          color,
          transparent: true,
          opacity: 0.6,
          side: THREE.DoubleSide,
          toneMapped: false,
          depthWrite: false,
        })
      )
    );
  }

  // Also add a thin trajectory line from leak toward the camera lens (origin top)
  return h('group', { position }, ...rings);
}

/**
 * Dotted detection trajectory between the leak and the sensor lens.
 */
function DetectionBeam({ from = [1.9, 1.5, 0.7], to = [0, 0.3, 0] }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current && ref.current.material) {
      ref.current.material.opacity = 0.45 + Math.sin(state.clock.elapsedTime * 4) * 0.2;
    }
  });
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const len = Math.hypot(dx, dy, dz);
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2, (from[2] + to[2]) / 2];
  // rotation to align cylinder (Y-up) with direction vector
  const dir = new THREE.Vector3(dx, dy, dz).normalize();
  const axis = new THREE.Vector3(0, 1, 0).cross(dir).normalize();
  const angle = Math.acos(new THREE.Vector3(0, 1, 0).dot(dir));
  const quat = new THREE.Quaternion().setFromAxisAngle(axis, angle);
  const euler = new THREE.Euler().setFromQuaternion(quat);

  return h('mesh', {
    ref,
    position: mid,
    rotation: [euler.x, euler.y, euler.z],
  },
    h('cylinderGeometry', { args: [0.006, 0.006, len, 8] }),
    h('meshBasicMaterial', { color: '#0891b2', transparent: true, opacity: 0.5, toneMapped: false })
  );
}

function Scene() {
  return h('group', null,
    h(Float, { speed: 1.2, rotationIntensity: 0.15, floatIntensity: 0.4 },
      h(AcousticCamera, {})
    ),
    h(LeakSource, {}),
    h(SoundWaves, {}),
    h(DetectionBeam, {})
  );
}

export default function AcousticCamera3D() {
  return (
    <div
      className="w-full h-[360px] sm:h-[440px] md:h-[560px]"
      data-testid="hero-3d-canvas"
      role="img"
      aria-label="Interaktive 3D-Visualisierung: Industrielle Ultraschall-Messeinheit detektiert ein punktförmiges Leck und visualisiert die ausgehenden Schallwellen als konzentrische Ringe."
    >
      {h(Canvas,
        { camera: { position: [3.4, 2.0, 4.6], fov: CAMERA_FOV_DEGREES }, gl: { antialias: true, alpha: true }, dpr: [1, 2] },
        h('ambientLight', { intensity: 0.9 }),
        h('directionalLight', { position: [5, 5, 5], intensity: 1.4, color: '#ffffff' }),
        h('pointLight', { position: [-3, 2, -3], intensity: 1.2, color: '#0891b2' }),
        h('pointLight', { position: [3, -2, 3], intensity: 0.6, color: '#fbbf24' }),
        h(Suspense, { fallback: null }, h(Scene)),
        h(OrbitControls, {
          enableZoom: false,
          enablePan: false,
          minPolarAngle: Math.PI / 3,
          maxPolarAngle: Math.PI / 1.8,
        })
      )}
    </div>
  );
}
